import Modal, { ModalSize } from "../../../../common/components/Modal";
import { type ReactNode, useMemo } from "react";
import { useAddToCartWizardContext } from "../AddToCartWizard";
import AtcProductInfo from "../../addToCart/AtcProductInfo";
import { useFormContext } from "react-hook-form";
import { type AddToCartFormData } from "../../../hooks/useAddToCartNew";
import GridGappedContainer, {
  GridGappedContainerSize,
} from "../../../../common/components/GridGappedContainer/GridGappedContainer";
import OptimeSolutionsMessage from "../../../../common/components/Messages/OptimeSolutionsMessage";
import {
  getCountriesListByProductDescriptor,
  isProductPMA,
} from "../../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { Permission, userHasAnyPermission, userHasPermission } from "../../../../utils/permissions";
import DisclaimerCountryOrigin from "../../../../common/components/Messages/DisclaimerCountryOrigin";
import { type HousingProduct, HousingProductType, isKITProduct } from "../../../../models/ProductData";
import classNames from "classnames";
import ProductDesignation from "../../../../ProductDetails/components/ProductDesignation";
import { Trans } from "react-i18next";
import { Translation } from "../../../../common/Translation";
import { HousingMessages } from "../../../../common/components/Messages/HousingMessages";
import useMediaQuery from "../../../../common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "../../../../utils/screenSizes";
import { type ColumnDef } from "@tanstack/react-table";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";

import { isNonNull } from "~/utils/reducers";
import { Alert } from "~/common/m3/Alert/Alert";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { Stacker } from "~/common/components/Stacker/Stacker";

interface Props {
  productDescriptor: ProductDescriptor;
  submit: () => void;
}

export const KITSummaryModal = ({ productDescriptor, submit }: Props) => {
  const { closePopup, getMainCls, t, getCloseBtn, getModalProps, getActionBtn, editMode } = useAddToCartWizardContext();

  const formProps = useFormContext<AddToCartFormData>();
  const watch = formProps.watch;

  const quantity = watch("quantity");
  const priceReference = watch("priceReference");
  const price = watch("netPrice");
  const priceUnit = watch("priceUnit");

  const optionalPartsForm = watch("optionalParts");

  if (!isKITProduct(productDescriptor.base)) {
    return null;
  }

  const { includedParts, optionalParts } = productDescriptor.base;
  const hasOptionalParts = Object.values(optionalPartsForm).some((part) => part.selected);
  const hasIncludedParts = !!includedParts.length;

  const selectedPack = watch("selectedPack");
  const isPackageSizeInvalid = quantity === 0 || (selectedPack && !Number.isInteger(quantity / selectedPack.packSize));

  const addToCartDisabled = isPackageSizeInvalid || !selectedPack;
  const isSubmitEnabled = !addToCartDisabled;

  let okLabel;
  if (editMode) {
    okLabel = "quoteListPage.applyBtn";
  } else {
    if (isProductPMA(productDescriptor)) {
      okLabel = "cartPage.btn.quote";
    } else {
      okLabel = "addToCartPopup.add.submitBtn";
    }
  }
  okLabel = t(okLabel);

  const showAddToCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  const hasSurcharge = watch("surcharge") > 0;

  return (
    <Modal
      size={ModalSize.MEDIUM}
      onClose={closePopup}
      footerChildren={
        <Stacker direction={"row"} gap={8}>
          {isDesktop && <>{getCloseBtn(t("cancel"))}</>}
          {showAddToCart && getActionBtn(okLabel, submit, !isSubmitEnabled)}
        </Stacker>
      }
      {...getModalProps()}
    >
      <div className="scha__atc-popup__wrapper">
        <div className={getMainCls()}>
          <div className="scha__atc-popup--column-info">
            <AtcProductInfo
              quantity={quantity}
              disabled={true}
              price={price}
              onChange={() => {}}
              priceUnit={priceUnit}
              priceReference={priceReference}
              descriptor={productDescriptor}
              hasSurcharge={hasSurcharge}
            />
          </div>
          <div className="scha__atc-popup--column-packaging__wrapper scha__atc-popup--column-packaging">
            <div className="scha__atc-popup--column-packaging">
              {hasIncludedParts && (
                <IncludedPartsTable
                  title={t("modal.atc.housing.included.title")}
                  items={includedParts}
                  kitQuantity={quantity}
                />
              )}
              {hasOptionalParts && (
                <OptionalPartsTable
                  className={"mt-30"}
                  title={t("modal.atc.housing.optional.title")}
                  items={optionalParts}
                />
              )}
            </div>
          </div>
        </div>
        <div className="notifications">
          <HousingMessages.EditQuantity />
          {isProductPMA(productDescriptor) && (
            <div className="mt-16">
              <InfoProductMessagePMA />
            </div>
          )}
          {userHasPermission(Permission.ADD_TO_CART) && (
            <Alert severity="info" className="scha-atc__delivery-disclaimer">
              {t("addToCartPopup.disclaimer")}
            </Alert>
          )}
          {productDescriptor.base.isOptime && <OptimeSolutionsMessage />}
          {getCountriesListByProductDescriptor({ descriptor: productDescriptor }).length > 0 && (
            <DisclaimerCountryOrigin />
          )}
        </div>
      </div>
    </Modal>
  );
};

interface PartsTableProps {
  className?: string;
  title: ReactNode;
  items: HousingProduct[];
}

const IncludedPartsTable = ({
  className,
  title,
  items: parts,
  kitQuantity,
}: PartsTableProps & { kitQuantity: number }) => {
  const items: DataTableRow[] = useMemo(
    () =>
      parts.map((item) => ({
        quantity: item.quantity * kitQuantity,
        description: getDescription(item),
        productId: getProductId(item),
      })),
    [parts, kitQuantity],
  );

  return (
    <div className={classNames("scha__atc__parts-table", className)}>
      <p className="scha__atc__parts-table__title">{title}</p>
      <TanStackTable data={items} columns={colConfig} fullwidth={true} variant={"clear"} />
    </div>
  );
};

const OptionalPartsTable = ({ className, title, items: parts }: PartsTableProps) => {
  const formProps = useFormContext<AddToCartFormData>();
  const formItems = formProps.watch("optionalParts");

  const items = useMemo(
    () =>
      parts
        .map((part) => {
          const formItem = formItems[part.id];
          if (!formItem?.selected) {
            return null;
          }
          return {
            quantity: formItem.quantity,
            description: getDescription(part),
            productId: getProductId(part),
          };
        })
        .filter(isNonNull),
    [parts, formItems],
  );

  return (
    <div className={classNames("scha__atc__parts-table", className)}>
      <p className="scha__atc__parts-table__title">{title}</p>
      <TanStackTable data={items} columns={colConfig} fullwidth={true} variant={"clear"} />
    </div>
  );
};

interface DataTableRow {
  quantity: number;
  productId: JSX.Element;
  description: JSX.Element | string;
}

const colConfig: ColumnDef<DataTableRow, number>[] = [
  {
    accessorKey: "quantity",
    header: () => <Trans i18nKey={"input.quantity"} />,
    size: 0.2,
    cell: (cell) => cell.renderValue(),
  },
  {
    accessorKey: "productId",
    header: () => <Trans i18nKey={"bulkUpload.table.header.productId"} />,
    size: 0.35,
    cell: (cell) => cell.renderValue(),
  },
  {
    accessorKey: "description",
    header: () => <Trans i18nKey={"cartPage.report.description"} />,
    size: 0.45,
    cell: (cell) => cell.renderValue(),
  },
];

const getDescription = (item: HousingProduct) =>
  item.type === HousingProductType.HOUSING ? "-" : <Translation {...item.product.shortDescription} />;
const getProductId = (item: HousingProduct) => (
  <ProductDesignation
    designation={item.product.designation}
    isPreferred={item.product.preferred}
    reservePreferredSpace={true}
    size={"label-large"}
  />
);
